import { formatCurrency } from "@npco/web-utils";
import {
	bindField,
	useForm,
	useFormValues,
} from "@npco/zeller-design-system/form";
import { useMemo } from "react";
import { z } from "zod";

import { Layout } from "../../shared/layout";
import type { Transactions } from "./get-transactions";
import { defaultLayoutProps } from "./render";
import { TransactionsList } from "./transactions-list";

export type SelectTransactionState = {
	amountAdjustable?: boolean;
	amount: number;
	transactions: Transactions;
	onConfirm: (transactionId: string) => void;
	onCancel: () => void;
};

const ValuesSchema = z.object({
	transactionId: z.string({ message: "" }).nonempty(""),
});

export const SelectTransaction = ({
	amountAdjustable,
	amount,
	onConfirm,
	onCancel,
	transactions,
}: SelectTransactionState) => {
	const form = useForm(
		ValuesSchema,
		({ value: { transactionId } }) => {
			/* v8 ignore next -- the schema rejects an empty selection and the
			   confirm action is disabled until a transaction is chosen, so the
			   submit handler only ever runs with a truthy transactionId; the guard
			   is defensive and its false branch is not reachable through the UI. */
			if (transactionId) {
				onConfirm(transactionId);
			}
		},
		{
			validators: {
				onMount: ValuesSchema as unknown as undefined,
				onChange: ValuesSchema as unknown as undefined,
			},
			defaultValues: {
				transactionId: undefined,
			},
		},
	);

	const transaction = useFormValues(
		form,
		["transactionId"],
		({ transactionId }) => transactions.find(({ id }) => id === transactionId),
	);

	const primaryActionLabel = useMemo(() => {
		if (amountAdjustable) {
			return "Confirm";
		}

		return `Refund ・ ${formatCurrency({
			amountInCents: amount,
			currency: transaction?.currency ?? transactions[0]?.currency,
		})}`;
	}, [amount, amountAdjustable, transaction, transactions]);

	const primaryActionDisabled = useMemo(() => {
		if (amountAdjustable) {
			return !transaction;
		}
		return !transaction || amount > transaction.amount;
	}, [amount, amountAdjustable, transaction]);

	return (
		<Layout
			{...defaultLayoutProps}
			title="Refund Transaction"
			actions={[
				{
					label: primaryActionLabel,
					onClick: form.handleSubmit,
					primary: true,
					disabled: primaryActionDisabled,
				},
				{
					label: "Cancel",
					onClick: onCancel,
				},
			]}
		>
			<div className="flex flex-col gap-16 px-32 pt-6">
				<form.Field name="transactionId">
					{(field) => (
						<TransactionsList
							amount={amount}
							amountAdjustable={amountAdjustable}
							disabled={undefined}
							message="We have found a few transactions that match your criteria. Please choose the transaction you would like to refund."
							readOnly={undefined}
							transactions={transactions}
							{...bindField(field)}
						/>
					)}
				</form.Field>
			</div>
		</Layout>
	);
};
